/**
 * Schema Validation Utilities
 * PHASE 4: Data Model Standardization
 * 
 * Provides runtime validation for Firestore documents to ensure data consistency
 */

// ============================================================================
// Schema Definitions
// ============================================================================

export const CONTACT_SCHEMA = {
  firstName: { type: 'string', required: true, maxLength: 100 },
  lastName: { type: 'string', required: true, maxLength: 100 },
  email: { type: 'string', required: false, format: 'email', maxLength: 255 },
  phone: { type: 'string', required: false, maxLength: 20 },
  isPrimary: { type: 'boolean', required: false },
  title: { type: 'string', required: false, maxLength: 100 },
  customerId: { type: 'string', required: true },
  focus: { type: 'array', required: false, itemType: 'string', maxItems: 10 }
};

export const CUSTOMER_SCHEMA = {
  customerName: { type: 'string', required: true, maxLength: 200 },
  customerEmail: { type: 'string', required: false, format: 'email', maxLength: 255 },
  customerPhone: { type: 'string', required: false, maxLength: 20 },
  customerStatus: { type: 'string', required: true, enum: ['Lead', 'Pending', 'Approved', 'Cancelled', 'Inactive'] },
  customerWebsite: { type: 'string', required: false, maxLength: 255 },
  customerAddress: { type: 'string', required: false, maxLength: 500 },
  customerCity: { type: 'string', required: false, maxLength: 100 },
  customerState: { type: 'string', required: false, maxLength: 2 },
  customerZip: { type: 'string', required: false, maxLength: 10 },
  businessType: { type: 'array', required: false, itemType: 'string', maxItems: 10, allowEmpty: true },
  businessFocus: { type: 'array', required: false, itemType: 'string', maxItems: 10, allowEmpty: true },
  salesReps: { type: 'array', required: false, itemType: 'object', maxItems: 50 }
};

export const OPPORTUNITY_SCHEMA = {
  customerId: { type: 'string', required: true },
  customerName: { type: 'string', required: true, maxLength: 200 },
  opportunityNumber: { type: 'number', required: false }, // Sequential counter value
  opportunityNumberLabel: { type: 'string', required: false }, // Human-readable ID (OPP-#)
  stage: { type: 'string', required: true, enum: ['New', 'Qualified', 'Not Qualified', 'Proposal Sent', 'Closed Won', 'Closed Lost'] },
  value: { type: 'string', required: false }, // Range key string (e.g., '10000-25000')
  actualValue: { type: 'number', required: false, min: 0 }, // Actual value for Closed Won
  startDate: { type: 'timestamp', required: true }, // Accepts Firestore Timestamp
  dueDate: { type: 'timestamp', required: false }, // Accepts Firestore Timestamp
  leadSource: { type: 'string', required: true },
  salesReps: { type: 'array', required: false, itemType: 'object', maxItems: 10 }
};

export const TASK_SCHEMA = {
  title: { type: 'string', required: true, maxLength: 200 },
  description: { type: 'string', required: false, maxLength: 2000 },
  status: { type: 'string', required: true, enum: ['open', 'completed', 'closed'] },
  assignedTo: { type: 'string', required: true },
  customerId: { type: 'string', required: false },
  opportunityId: { type: 'string', required: false },
  dueDate: { type: 'timestamp', required: false }
};

export const ORDER_SCHEMA = {
  order_number: { type: 'string', required: true },
  date_internal: { type: 'string', required: true, format: 'date' },
  customer_id: { type: 'string', required: true },
  customer_name: { type: 'string', required: true, maxLength: 200 },
  total: { type: 'number', required: true, min: 0 },
  amount_open: { type: 'number', required: false, min: 0 },
  amount_shipped: { type: 'number', required: false, min: 0 },
  status: { type: 'string', required: true },
  commissions_displayNames: { type: 'array', required: false, itemType: 'string', maxItems: 50 }
};

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate email format
 */
function isValidEmail(email) {
  if (typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate date string (YYYY-MM-DD)
 */
function isValidDateString(dateStr) {
  if (typeof dateStr !== 'string') return false;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
}

/**
 * Validate Firestore Timestamp
 */
function isValidTimestamp(value) {
  return value && typeof value.toDate === 'function';
}

/**
 * Main validation function
 * @param {Object} data - Data object to validate
 * @param {Object} schema - Schema definition
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateSchema(data, schema) {
  const errors = [];
  
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Data must be an object'] };
  }
  
  // Check each field in schema
  Object.entries(schema).forEach(([field, rules]) => {
    const value = data[field];
    
    // Required field check
    if (rules.required && (value === undefined || value === null || value === '')) {
      errors.push(`${field} is required`);
      return; // Skip further validation if required field is missing
    }
    
    // Skip validation if field is optional and not provided
    if (!rules.required && (value === undefined || value === null)) {
      return;
    }
    
    // Type validation
    if (rules.type) {
      switch (rules.type) {
        case 'string':
          if (typeof value !== 'string') {
            errors.push(`${field} must be a string`);
          } else if (rules.maxLength && value.length > rules.maxLength) {
            errors.push(`${field} must be ${rules.maxLength} characters or less`);
          }
          break;
          
        case 'number':
          if (typeof value !== 'number' || isNaN(value)) {
            errors.push(`${field} must be a number`);
          } else {
            if (rules.min !== undefined && value < rules.min) {
              errors.push(`${field} must be at least ${rules.min}`);
            }
            if (rules.max !== undefined && value > rules.max) {
              errors.push(`${field} must be at most ${rules.max}`);
            }
          }
          break;
          
        case 'boolean':
          if (typeof value !== 'boolean') {
            errors.push(`${field} must be a boolean`);
          }
          break;
          
        case 'array':
          if (!Array.isArray(value)) {
            errors.push(`${field} must be an array`);
          } else {
            // Allow empty arrays if allowEmpty flag is set
            if (value.length === 0 && rules.allowEmpty) {
              break;
            }
            
            if (rules.maxItems && value.length > rules.maxItems) {
              errors.push(`${field} can have at most ${rules.maxItems} items`);
            }
            // Validate array item types
            if (rules.itemType) {
              value.forEach((item, index) => {
                if (typeof item !== rules.itemType) {
                  errors.push(`${field}[${index}] must be of type ${rules.itemType}`);
                }
              });
            }
          }
          break;
          
        case 'timestamp':
          if (!isValidTimestamp(value)) {
            errors.push(`${field} must be a valid Firestore Timestamp`);
          }
          break;
          
        default:
          // Generic type check
          if (typeof value !== rules.type) {
            errors.push(`${field} must be of type ${rules.type}`);
          }
      }
    }
    
    // Format validation
    if (value && rules.format) {
      switch (rules.format) {
        case 'email':
          if (!isValidEmail(value)) {
            errors.push(`${field} must be a valid email address`);
          }
          break;
          
        case 'date':
          if (!isValidDateString(value)) {
            errors.push(`${field} must be a valid date (YYYY-MM-DD)`);
          }
          break;
        default:
          break;
      }
    }
    
    // Enum validation
    if (rules.enum && value !== undefined && value !== null) {
      if (!rules.enum.includes(value)) {
        errors.push(`${field} must be one of: ${rules.enum.join(', ')}`);
      }
    }
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate and sanitize data before Firestore write
 * @param {Object} data - Data to validate
 * @param {Object} schema - Schema definition
 * @returns {Object} { valid: boolean, sanitized: Object, errors: string[] }
 */
export function validateAndSanitize(data, schema) {
  const validation = validateSchema(data, schema);
  
  if (!validation.valid) {
    return {
      valid: false,
      sanitized: null,
      errors: validation.errors
    };
  }
  
  // Create sanitized copy with only schema-defined fields
  const sanitized = {};
  
  Object.keys(schema).forEach(field => {
    if (data[field] !== undefined && data[field] !== null) {
      sanitized[field] = data[field];
    }
  });
  
  return {
    valid: true,
    sanitized,
    errors: []
  };
}

/**
 * Get schema for entity type
 * @param {string} entityType - 'contact', 'customer', 'opportunity', 'task', 'order'
 * @returns {Object|null} Schema definition or null if not found
 */
export function getSchemaForEntity(entityType) {
  const schemas = {
    contact: CONTACT_SCHEMA,
    customer: CUSTOMER_SCHEMA,
    opportunity: OPPORTUNITY_SCHEMA,
    task: TASK_SCHEMA,
    order: ORDER_SCHEMA
  };
  
  return schemas[entityType.toLowerCase()] || null;
}

/**
 * Validate entity by type
 * @param {Object} data - Data to validate
 * @param {string} entityType - Entity type
 * @returns {Object} Validation result
 */
export function validateEntity(data, entityType) {
  const schema = getSchemaForEntity(entityType);
  
  if (!schema) {
    return {
      valid: false,
      errors: [`Unknown entity type: ${entityType}`]
    };
  }
  
  return validateSchema(data, schema);
}



